import { useEffect, useRef, useState } from "react";

import { Label } from "@/components/ui/label";
import { useSlideStore } from "@/stores/slide-store";
import type { SlideInfo } from "@/stores/slide-store";
import { claudeAgentApi } from "@/lib/api/claude-agent";

interface SlideNotesEditorProps {
  slide: SlideInfo;
  devContainerId?: string;
  getIdToken: () => Promise<string | null>;
}

const DEBOUNCE_MS = 500;

// Per-slide speaker notes editor. Mounted once at the bottom of the slides
// panel and re-bound to whichever slide is currently selected.
//
// State flow:
//   1. User types → local `draft` updates synchronously (no input lag)
//   2. 500ms after last keystroke → optimistic store update via
//      `updateSlideConfig` (so PresenterDashboard's notes pane refreshes
//      immediately) + PUT to sidecar
//   3. External `speakerNotes` changes (agent edits slides.json, slide
//      switch, manifest refetch) sync into `draft` via the
//      `slide.id`-keyed effect.
//
// Switching slides mid-debounce: the cleanup clears the pending timer,
// so any unsaved keystrokes for the previous slide are lost. Rare in
// practice (debounce is short); accepted tradeoff for simpler code.
export function SlideNotesEditor({
  slide,
  devContainerId,
  getIdToken,
}: SlideNotesEditorProps) {
  const [draft, setDraft] = useState<string>(slide.speakerNotes ?? "");
  const lastSavedRef = useRef<string>(slide.speakerNotes ?? "");

  // Sync external changes (slide switch, agent edits, refetch) into the
  // textarea. Keyed on slide.id so a switch always re-seeds the draft.
  useEffect(() => {
    setDraft(slide.speakerNotes ?? "");
    lastSavedRef.current = slide.speakerNotes ?? "";
  }, [slide.id, slide.speakerNotes]);

  // Debounced save — fires 500ms after the last keystroke if draft differs
  // from what's already saved. Closure captures slide.id so a switch mid-
  // debounce targets the right slide.
  useEffect(() => {
    if (draft === lastSavedRef.current) return;
    const slideId = slide.id;
    const next = draft;
    const t = setTimeout(async () => {
      lastSavedRef.current = next;
      useSlideStore
        .getState()
        .updateSlideConfig(slideId, { speakerNotes: next || null });
      if (!devContainerId) return;
      try {
        const token = await getIdToken();
        if (!token) return;
        await claudeAgentApi.updateSlideConfig(
          token,
          devContainerId,
          slideId,
          { speakerNotes: next || null },
        );
      } catch (err) {
        console.error("Failed to save speaker notes:", err);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [draft, slide.id, devContainerId, getIdToken]);

  return (
    <div className="border-t border-border bg-card p-2 flex flex-col gap-1.5">
      <Label
        htmlFor={`notes-${slide.id}`}
        className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider"
      >
        Speaker notes
      </Label>
      <textarea
        id={`notes-${slide.id}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Notes for this slide…"
        rows={4}
        className="resize-none w-full text-sm leading-relaxed text-foreground bg-background border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground/70"
      />
    </div>
  );
}
